import React, { useState, useEffect } from 'react';
import {
  IconEdit, IconTrash, IconPlus, IconArrowRight,
  IconCheck, IconX, IconAlertCircle, IconSortAscending,
  IconArrowsExchange, IconLoader
} from '@tabler/icons-react';
import styles from '../../pages/AdminDashboard.module.css';
import { categoryService, Category } from '../../services/categoryService';
import { adminService } from '../../services/adminApi';

// Interface des props du composant
interface CategoryManagementProps {
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

// Composant principal de gestion des catégories
const CategoryManagement: React.FC<CategoryManagementProps> = ({ onSuccess, onError }) => {
  // État des catégories
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategory, setNewCategory] = useState<string>('');
  const [newCategoryColor, setNewCategoryColor] = useState<string>('#ADB5BD');
  const [newCategoryIcon, setNewCategoryIcon] = useState<string>('');
  const [editCategory, setEditCategory] = useState<Category | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statsMode, setStatsMode] = useState<boolean>(false);
  
  // État pour la migration de catégorie
  const [showMigrationModal, setShowMigrationModal] = useState<boolean>(false);
  const [sourceCategory, setSourceCategory] = useState<string>('');
  const [targetCategory, setTargetCategory] = useState<string>('');
  const [migrationLoading, setMigrationLoading] = useState<boolean>(false);
  const [migrationResult, setMigrationResult] = useState<{success: number; errors: any[]} | null>(null);
  
  // Charger les catégories au démarrage
  useEffect(() => {
    // Charger automatiquement avec les statistiques au démarrage
    loadCategories(true);
    setStatsMode(true);
  }, []);

  // Fonction pour charger les catégories
  const loadCategories = async (showStats = false) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Chargement des catégories avec showStats =', showStats, 'statsMode =', statsMode);
      
      // Récupérer les catégories depuis le service
      const categoriesFromService = showStats || statsMode 
        ? await categoryService.getCategoriesStats()
        : await categoryService.getCategories(true);
      
      console.log('Catégories chargées:', categoriesFromService);
      console.log('Vérification des eventCount:', categoriesFromService.map(c => ({ 
        id: c.id, 
        name: c.name, 
        eventCount: c.eventCount 
      })));
        
      setCategories(categoriesFromService);
    } catch (err: any) {
      console.error('Erreur lors du chargement des catégories:', err);
      setError(`Erreur lors du chargement des catégories: ${err.message || 'Erreur inconnue'}`);
      if (onError) onError(`Erreur lors du chargement des catégories: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ajouter une catégorie
  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      setError('Le nom de la catégorie ne peut pas être vide');
      if (onError) onError('Le nom de la catégorie ne peut pas être vide');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Utiliser le service pour ajouter la catégorie avec les options étendues
      await categoryService.addCategory(newCategory, {
        color: newCategoryColor,
        icon: newCategoryIcon
      });
      
      // Recharger les catégories pour avoir la liste à jour
      await loadCategories();
      
      // Réinitialiser le formulaire
      setNewCategory('');
      setNewCategoryColor('#ADB5BD');
      setNewCategoryIcon('');
      
      // Message de succès
      const successMessage = `Catégorie "${newCategory}" ajoutée avec succès`;
      console.log(successMessage);
      if (onSuccess) onSuccess(successMessage);
    } catch (err: any) {
      console.error('Erreur lors de l\'ajout de la catégorie:', err);
      setError(`Erreur lors de l'ajout de la catégorie: ${err.message || 'Erreur inconnue'}`);
      if (onError) onError(`Erreur lors de l'ajout de la catégorie: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour une catégorie
  const handleUpdateCategory = async () => {
    if (!editCategory) return;
    if (!editCategory.name.trim()) {
      setError('Le nom de la catégorie ne peut pas être vide');
      if (onError) onError('Le nom de la catégorie ne peut pas être vide');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Mettre à jour la catégorie avec toutes les propriétés
      await categoryService.updateCategory(editCategory.id, {
        name: editCategory.name,
        color: editCategory.color,
        icon: editCategory.icon
      });
      
      // Recharger les catégories
      await loadCategories();
      
      // Réinitialiser l'édition
      setEditCategory(null);
      
      // Message de succès
      const successMessage = `Catégorie "${editCategory.name}" mise à jour avec succès`;
      console.log(successMessage);
      if (onSuccess) onSuccess(successMessage);
    } catch (err: any) {
      console.error('Erreur lors de la mise à jour de la catégorie:', err);
      setError(`Erreur lors de la mise à jour de la catégorie: ${err.message || 'Erreur inconnue'}`);
      if (onError) onError(`Erreur lors de la mise à jour de la catégorie: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour supprimer une catégorie
  const handleDeleteCategory = async (categoryId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ? Cette action est irréversible.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Supprimer la catégorie
      await categoryService.deleteCategory(categoryId);
      
      // Recharger les catégories
      await loadCategories();
      
      // Message de succès
      const successMessage = 'Catégorie supprimée avec succès';
      console.log(successMessage);
      if (onSuccess) onSuccess(successMessage);
    } catch (err: any) {
      console.error('Erreur lors de la suppression de la catégorie:', err);
      setError(`Erreur lors de la suppression de la catégorie: ${err.message || 'Erreur inconnue'}`);
      if (onError) onError(`Erreur lors de la suppression de la catégorie: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };

  // Initialiser les catégories par défaut
  const handleInitializeDefaultCategories = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir initialiser les catégories par défaut ? Cela ne supprimera pas vos catégories personnalisées.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Appel à l'API pour initialiser les catégories par défaut
      await fetch('/api/event-categories/init', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Recharger les catégories
      await loadCategories();
      
      // Message de succès
      const successMessage = 'Catégories par défaut initialisées avec succès';
      console.log(successMessage);
      if (onSuccess) onSuccess(successMessage);
    } catch (err: any) {
      console.error('Erreur lors de l\'initialisation des catégories par défaut:', err);
      setError(`Erreur lors de l'initialisation des catégories par défaut: ${err.message || 'Erreur inconnue'}`);
      if (onError) onError(`Erreur lors de l'initialisation des catégories par défaut: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Basculer entre le mode statistique et le mode normal
  const toggleStatsMode = () => {
    setStatsMode(!statsMode);
    loadCategories(!statsMode);
  };
  
  // Fonction pour gérer la migration de catégories
  const handleMigrationModalOpen = () => {
    setShowMigrationModal(true);
    setMigrationResult(null);
  };
  
  const handleMigrationModalClose = () => {
    setShowMigrationModal(false);
    setSourceCategory('');
    setTargetCategory('');
    setMigrationResult(null);
  };
  
  const handleMigrateCategories = async () => {
    if (!sourceCategory || !targetCategory || sourceCategory === targetCategory) {
      setError('Veuillez sélectionner deux catégories différentes pour la migration');
      if (onError) onError('Veuillez sélectionner deux catégories différentes pour la migration');
      return;
    }
    
    try {
      setMigrationLoading(true);
      setError(null);
      
      // Appeler le service de migration
      const result = await adminService.migrateEventCategories(sourceCategory, targetCategory);
      
      // Afficher le résultat
      setMigrationResult(result);
      
      // Recharger les statistiques des catégories
      if (result.success > 0) {
        await loadCategories(true);
        
        // Message de succès
        const successMessage = `${result.success} événement(s) migré(s) avec succès`;
        console.log(successMessage);
        if (onSuccess) onSuccess(successMessage);
      }
      
      // S'il y a des erreurs, les afficher
      if (result.errors.length > 0) {
        const errorMessage = `Erreur lors de la migration de ${result.errors.length} événement(s)`;
        console.error(errorMessage, result.errors);
        if (onError) onError(errorMessage);
        setError(errorMessage);
      }
    } catch (err: any) {
      console.error('Erreur lors de la migration des catégories:', err);
      setError(`Erreur lors de la migration des catégories: ${err.message || 'Erreur inconnue'}`);
      if (onError) onError(`Erreur lors de la migration des catégories: ${err.message || 'Erreur inconnue'}`);
    } finally {
      setMigrationLoading(false);
    }
  };

  return (
    <div className={styles.componentWrapper}>
      <div className={styles.componentHeader}>
        <h2>Gestion des catégories d'événements</h2>
        <div className={styles.componentActions}>
          <button 
            className={`${styles.actionButton} ${statsMode ? styles.actionButtonActive : ''}`}
            onClick={toggleStatsMode}
            title="Afficher les statistiques"
          >
            <IconSortAscending size={20} />
            {statsMode ? 'Masquer les stats' : 'Afficher les stats'}
          </button>
          <button 
            className={styles.actionButton}
            onClick={handleInitializeDefaultCategories}
            disabled={loading}
            title="Initialiser les catégories par défaut"
          >
            <IconPlus size={20} />
            Initialiser par défaut
          </button>
        </div>
      </div>
      
      {error && (
        <div className={styles.errorMessage}>
          <IconAlertCircle size={20} />
          {error}
        </div>
      )}
      
      <div className={styles.categoryList}>
        {loading ? (
          <div className={styles.loadingState}>Chargement des catégories...</div>
        ) : categories.length === 0 ? (
          <div className={styles.emptyState}>Aucune catégorie disponible</div>
        ) : (
          <>
            <div className={styles.categoryHeader}>
              <div className={styles.categoryHeaderItem} style={{ flex: 0.5 }}>Couleur</div>
              <div className={styles.categoryHeaderItem}>Nom</div>
              <div className={styles.categoryHeaderItem}>Code</div>
              {statsMode && <div className={styles.categoryHeaderItem}>Événements</div>}
              <div className={styles.categoryHeaderItem} style={{ flex: 0.5 }}>Actions</div>
            </div>
            
            {categories.map(category => (
              <div key={category.id} className={styles.categoryItem}>
                {editCategory && editCategory.id === category.id ? (
                  // Mode édition
                  <>
                    <div className={styles.categoryItemProperty} style={{ flex: 0.5 }}>
                      <input
                        type="color"
                        value={editCategory.color || '#ADB5BD'}
                        onChange={(e) => setEditCategory({...editCategory, color: e.target.value})}
                        className={styles.colorInput}
                      />
                    </div>
                    <div className={styles.categoryItemProperty}>
                      <input
                        type="text"
                        value={editCategory.name}
                        onChange={(e) => setEditCategory({...editCategory, name: e.target.value})}
                        className={styles.editInput}
                      />
                    </div>
                    <div className={styles.categoryItemProperty}>
                      <input
                        type="text"
                        value={editCategory.icon || ''}
                        onChange={(e) => setEditCategory({...editCategory, icon: e.target.value})}
                        className={styles.editInput}
                        placeholder="Icône (optionnelle)"
                      />
                    </div>
                    <div className={styles.categoryItemProperty} style={{ flex: 0.5 }}>
                      <button
                        onClick={handleUpdateCategory}
                        className={styles.actionButton}
                        disabled={loading}
                        title="Enregistrer"
                      >
                        <IconCheck size={18} />
                      </button>
                      <button
                        onClick={() => setEditCategory(null)}
                        className={styles.actionButton}
                        title="Annuler"
                      >
                        <IconX size={18} />
                      </button>
                    </div>
                  </>
                ) : (
                  // Mode affichage
                  <>
                    <div className={styles.categoryItemProperty} style={{ flex: 0.5 }}>
                      <div 
                        className={styles.colorSwatch} 
                        style={{ backgroundColor: category.color || '#ADB5BD' }}
                        title={category.color || 'Aucune couleur définie'}
                      />
                    </div>
                    <div className={styles.categoryItemProperty}>
                      {category.name}
                      {category.isDefault && <span className={styles.defaultBadge}>Par défaut</span>}
                    </div>
                    <div className={styles.categoryItemProperty}>
                      <code>{category.id}</code>
                    </div>
                    {statsMode && (
                      <div className={styles.categoryItemProperty}>
                        {category.eventCount || 0} événement(s)
                      </div>
                    )}
                    <div className={styles.categoryItemProperty} style={{ flex: 0.5 }}>
                      <button
                        onClick={() => setEditCategory(category)}
                        className={styles.actionButton}
                        disabled={loading}
                        title="Modifier"
                      >
                        <IconEdit size={18} />
                      </button>
                      {!category.isDefault && (
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className={styles.actionButton}
                          disabled={loading}
                          title="Supprimer"
                        >
                          <IconTrash size={18} />
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </>
        )}
      </div>
      
      <div className={styles.adminToolbar}>
        <div>
          <button 
            className={`${styles.toolbarButton} ${statsMode ? styles.active : ''}`}
            onClick={toggleStatsMode}
          >
            <IconSortAscending size={18} />
            {statsMode ? 'Masquer les statistiques' : 'Afficher les statistiques'}
          </button>
          
          <button 
            className={styles.toolbarButton}
            onClick={handleMigrationModalOpen}
            disabled={loading || categories.length < 2}
          >
            <IconArrowsExchange size={18} />
            Migrer les événements
          </button>
        </div>
        
        <button 
          className={styles.dangerButton} 
          onClick={handleInitializeDefaultCategories}
          disabled={loading}
        >
          <IconAlertCircle size={18} />
          Réinitialiser les catégories
        </button>
      </div>
      
      <div className={styles.formWrapper}>
        <h3>Ajouter une catégorie</h3>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="categoryColor">Couleur</label>
            <input
              type="color"
              id="categoryColor"
              value={newCategoryColor}
              onChange={(e) => setNewCategoryColor(e.target.value)}
              className={styles.colorInput}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="categoryName">Nom</label>
            <input
              type="text"
              id="categoryName"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Nom de la catégorie"
              className={styles.formInput}
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="categoryIcon">Icône (optionnelle)</label>
            <input
              type="text"
              id="categoryIcon"
              value={newCategoryIcon}
              onChange={(e) => setNewCategoryIcon(e.target.value)}
              placeholder="Nom de l'icône"
              className={styles.formInput}
            />
          </div>
          <div className={styles.formActions}>
            <button
              onClick={handleAddCategory}
              className={styles.primaryButton}
              disabled={loading || !newCategory.trim()}
            >
              <IconPlus size={18} />
              Ajouter
            </button>
          </div>
        </div>
      </div>
      
      {/* Modal de migration de catégories */}
      {showMigrationModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>
              <IconArrowsExchange size={20} /> 
              Migration d'événements entre catégories
            </h3>
            
            <div className={styles.modalContent}>
              <p className={styles.modalDescription}>
                Cette opération va déplacer tous les événements d'une catégorie vers une autre.
                L'opération est irréversible. Assurez-vous de bien vérifier votre sélection.
              </p>
              
              {error && (
                <div className={styles.errorMessage}>
                  <IconAlertCircle size={16} />
                  {error}
                </div>
              )}
              
              {migrationResult && (
                <div className={styles.resultSummary}>
                  <p className={styles.successMessage}>
                    <IconCheck size={16} />
                    {migrationResult.success} événement(s) migré(s) avec succès
                  </p>
                  
                  {migrationResult.errors.length > 0 && (
                    <div className={styles.errorSummary}>
                      <p>{migrationResult.errors.length} erreur(s) lors de la migration</p>
                      <ul>
                        {migrationResult.errors.slice(0, 5).map((error, idx) => (
                          <li key={idx}>
                            Événement "{error.title}" (ID: {error.eventId}): {error.error}
                          </li>
                        ))}
                        {migrationResult.errors.length > 5 && (
                          <li>...et {migrationResult.errors.length - 5} autres erreurs</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              <div className={styles.formGroup}>
                <label htmlFor="sourceCategory">Catégorie source</label>
                <select
                  id="sourceCategory"
                  value={sourceCategory}
                  onChange={(e) => setSourceCategory(e.target.value)}
                  className={styles.formInput}
                  disabled={migrationLoading}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(category => (
                    <option key={`source-${category.id}`} value={category.id}>
                      {category.name} ({category.eventCount || 0} événement(s))
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={styles.formGroup}>
                <IconArrowRight size={24} className={styles.migrationArrow} />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="targetCategory">Catégorie cible</label>
                <select
                  id="targetCategory"
                  value={targetCategory}
                  onChange={(e) => setTargetCategory(e.target.value)}
                  className={styles.formInput}
                  disabled={migrationLoading || !sourceCategory}
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories
                    .filter(cat => cat.id !== sourceCategory)
                    .map(category => (
                      <option key={`target-${category.id}`} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>
            
            <div className={styles.modalActions}>
              <button
                onClick={handleMigrationModalClose}
                className={styles.secondaryButton}
                disabled={migrationLoading}
              >
                Annuler
              </button>
              
              <button
                onClick={handleMigrateCategories}
                className={styles.primaryButton}
                disabled={migrationLoading || !sourceCategory || !targetCategory || sourceCategory === targetCategory}
              >
                {migrationLoading ? (
                  <>
                    <IconLoader size={18} className={styles.spinnerIcon} />
                    Migration en cours...
                  </>
                ) : (
                  <>
                    <IconArrowsExchange size={18} />
                    Migrer les événements
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
